import { useAuth0 } from "@auth0/auth0-react";
import { DqLoader } from "@decentriq/components";
import {
  type TableColumnFormatType,
  type TableColumnHashingAlgorithm,
} from "@decentriq/graphql/dist/types";
import { data_room as ddcDataRoom } from "ddc";
import { type SnackbarKey } from "notistack";
import { memo, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useMediaDataRoomQuery } from "features/mediaDataRoom/hooks";
import {
  type MediaDataRoomAb,
  MediaDataRoomVersion,
  PERFORMANCE_REPORT_ALLOWED_USERS,
} from "features/mediaDataRoom/models";
import { mapMediaDataRoomErrorToSnackbar, useDataRoomSnackbar } from "hooks";
import {
  MediaDataRoomContext,
  type MediaDataRoomV3ContextValue,
} from "../models";
import useParticipantsBranding from "./useParticipantsWithBranding";

export type MediaDataRoomV3WrapperProps = React.PropsWithChildren<{
  data: MediaDataRoomAb;
}>;

const MediaDataRoomV3Wrapper = memo<MediaDataRoomV3WrapperProps>(
  ({ data: publishedMediaDataRoom, children }) => {
    const { enqueueSnackbar, closeSnackbar } = useDataRoomSnackbar();
    const setErrorSnackbarId = useState<SnackbarKey | undefined>()[1];
    const {
      data,
      error,
      isLoading: isMediaDataRoomLoading,
    } = useMediaDataRoomQuery({
      dataRoomId: publishedMediaDataRoom.id,
    });
    const { user } = useAuth0();
    const currentUserEmail = user?.email ?? "";
    const currentUserPermissions = useMemo(
      () =>
        data?.state.participantGroups.find(({ emails }) =>
          emails.includes(currentUserEmail)
        )?.permissions ?? [],
      [data?.state.participantGroups, currentUserEmail]
    );
    const { data: participants, isLoading: isParticipantsLoading } =
      useParticipantsBranding({
        createdBy: publishedMediaDataRoom.owner?.email ?? "",
        currentUserEmail,
        dataRoomId: publishedMediaDataRoom.id,
        ownerOrgLogo:
          publishedMediaDataRoom.ownerOrganization?.logo ?? undefined,
        ownerOrgName: publishedMediaDataRoom.ownerOrganization?.name ?? "",
        participantGroups: data?.state.participantGroups?.filter(
          ({ emails }) =>
            !emails.some((email) =>
              PERFORMANCE_REPORT_ALLOWED_USERS.includes(email)
            )
        ),
      });
    const isLoading = isMediaDataRoomLoading || isParticipantsLoading;
    const baseAudienceProviderName = useMemo(
      () =>
        participants?.find(({ permissions }) =>
          permissions.includes(ddcDataRoom.Permission.ProvideBaseAudience)
        )?.name ?? "",
      [participants]
    );
    const seedAudienceProviderName = useMemo(
      () =>
        participants?.find(({ permissions }) =>
          permissions.includes(ddcDataRoom.Permission.ProvideSeedAudience)
        )?.name ?? "",
      [participants]
    );
    useEffect(() => {
      if (error) {
        const snackbarId = enqueueSnackbar(
          ...mapMediaDataRoomErrorToSnackbar(
            error,
            `Failed to fetch media data clean room`
          )
        );
        setErrorSnackbarId(snackbarId);
      } else {
        setErrorSnackbarId((snackbarId) => {
          if (snackbarId) {
            closeSnackbar(snackbarId);
          }
          return undefined;
        });
      }
    }, [enqueueSnackbar, closeSnackbar, setErrorSnackbarId, error]);
    const contextValue = useMemo<MediaDataRoomV3ContextValue | null>(() => {
      if (!data) {
        return null;
      }
      const media = data.state;
      const showAbsoluteValues = !media.features.includes(
        ddcDataRoom.Features.EnableHideAbsoluteAudienceSizes
      );
      const canCreateAudience = currentUserPermissions.includes(
        ddcDataRoom.Permission.CreateCustomAudience
      );
      const canExportAudience = currentUserPermissions.includes(
        ddcDataRoom.Permission.ExportAudience
      );
      const value: MediaDataRoomV3ContextValue = {
        baseAudienceProviderName,
        createdAt: data.createdAt,
        createdBy: publishedMediaDataRoom.owner?.email ?? "",
        currentUserEmail,
        dataRoomId: data.id,
        dataRoomName: media.name,
        driverAttestationHash: data.driverAttestationHash,
        hashMatchingIdWith:
          media.hashMatchingIdWith as unknown as TableColumnHashingAlgorithm | null,
        isDeactivated: Boolean(publishedMediaDataRoom.deactivatedAt),
        isStopped: Boolean(publishedMediaDataRoom.isStopped),
        loading: false,
        matchingIdFormat:
          media.matchingIdFormat as unknown as TableColumnFormatType,
        participants: participants ?? [],
        seedAudienceProviderName,
        supportedFeatures: {
          canCreateAudience,
          canDeleteAudience: currentUserPermissions.includes(
            ddcDataRoom.Permission.CreateCustomAudience
          ),
          canExportAudience,
          canProvideBaseAudience: currentUserPermissions.includes(
            ddcDataRoom.Permission.ProvideBaseAudience
          ),
          canProvideSeedAudience: currentUserPermissions.includes(
            ddcDataRoom.Permission.ProvideSeedAudience
          ),
          canShareAudience: currentUserPermissions.includes(
            ddcDataRoom.Permission.CreateCustomAudience
          ),
          canStopMediaDataRoom: true,
          canViewAudienceStatus: true,
          canViewAudiences: canCreateAudience || canExportAudience,
          canViewBaseAudienceDetails: currentUserPermissions.includes(
            ddcDataRoom.Permission.ProvideBaseAudience
          ),
          canViewInsights: currentUserPermissions.includes(
            ddcDataRoom.Permission.ViewInsights
          ),
          canViewSeedAudienceDetails: currentUserPermissions.includes(
            ddcDataRoom.Permission.ProvideSeedAudience
          ),
          enableDropInvalidRows: true,
          enableInsights: media.features.includes(
            ddcDataRoom.Features.EnableInsights
          ),
          enableLookalike: media.features.includes(
            ddcDataRoom.Features.EnableLookalikeAudiences
          ),
          enableOverlapStatistics: {
            canView:
              showAbsoluteValues &&
              currentUserPermissions.includes(
                ddcDataRoom.Permission.ViewOverlap
              ),
            enabled: currentUserPermissions.includes(
              ddcDataRoom.Permission.ViewOverlap
            ),
          },
          enableRemarketing: media.features.includes(
            ddcDataRoom.Features.EnableRemarketing
          ),
          enableRuleBasedAudiences: media.features.includes(
            ddcDataRoom.Features.EnableRuledBasedAudiences
          ),
          performanceReportEnabled: true,
          showAbsoluteValues,
        },
        version: MediaDataRoomVersion.v3,
      };
      return value;
    }, [
      data,
      currentUserEmail,
      participants,
      currentUserPermissions,
      publishedMediaDataRoom.deactivatedAt,
      publishedMediaDataRoom.owner?.email,
      publishedMediaDataRoom.isStopped,
      baseAudienceProviderName,
      seedAudienceProviderName,
    ]);
    if (isLoading) {
      return (
        <DqLoader
          sx={{
            alignItems: "center",
            height: "100%",
            justifyContent: "center",
            width: "100%",
          }}
        />
      );
    }
    if (error || !data) {
      return <Navigate replace={true} to="/datarooms" />;
    }
    return (
      <MediaDataRoomContext.Provider value={contextValue}>
        {children}
      </MediaDataRoomContext.Provider>
    );
  }
);
MediaDataRoomV3Wrapper.displayName = "MediaDataRoomV3Wrapper";

export default MediaDataRoomV3Wrapper;
